import React from "react";
import {
  Avatar, Badge, Box, Flex, Icon, Spinner, Switch, Table, TableContainer,
  Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
} from "@chakra-ui/react";
import { MdStar } from "react-icons/md";
import Card from "horizon/components/card/Card";
import { ARTIST_TYPE_LABELS, formatServiceLabel, formatServicePrice } from "./servicesManageConstants";
import { BODY_CELL, HEAD_CELL, TYPE_BADGE } from "./servicesManageStyles";

const COLUMNS = ["Service", "Propriétaire", "Pays", "Prix", "Visible", "Test", "Notes"];

/**
 * Renders the "Service" cell content from events + thematics + country.
 * @param {Object} props - service object
 * @returns {React.ReactElement}
 */
function ServiceLabelCell({ service }) {
  const nameColor = useColorModeValue("secondaryGray.900", "white");
  const idColor = useColorModeValue("secondaryGray.500", "secondaryGray.500");
  return (
    <Flex direction="column" minW="0">
      <Text noOfLines={1} fontSize="sm" fontWeight="600" color={nameColor}>{formatServiceLabel(service)}</Text>
      <Text fontSize="xs" fontFamily="mono" color={idColor}>#{service?.id || "—"}</Text>
    </Flex>
  );
}

/**
 * Renders the "Propriétaire" cell with avatar + name + type badge.
 * Falls back to raw user_id / artist_group_id when enrichment is missing.
 * @param {Object} props - service object
 * @returns {React.ReactElement}
 */
function OwnerCell({ service }) {
  const nameColor = useColorModeValue("secondaryGray.900", "white");
  const name = service?.artist_name;
  const typeLabel = ARTIST_TYPE_LABELS[service?.artist_type] || service?.artist_type || "—";
  const fallbackId = service?.artist_group_id
    ? `Groupe #${service.artist_group_id}`
    : (service?.user_id ? `Artiste #${service.user_id}` : "—");
  return (
    <Flex align="center" gap="10px" minW="0">
      <Avatar src={service?.artist_photo} name={name} w="28px" h="28px" size="sm" />
      <Flex direction="column" minW="0">
        <Text noOfLines={1} fontSize="sm" fontWeight="600" color={nameColor}>{name || fallbackId}</Text>
        <Badge colorScheme="brand" variant="subtle" {...TYPE_BADGE}>{typeLabel}</Badge>
      </Flex>
    </Flex>
  );
}

/**
 * Renders the notes cell (average rating with star icon).
 * @param {Object} props - service object
 * @returns {React.ReactElement}
 */
function NotesCell({ service }) {
  const color = useColorModeValue("secondaryGray.900", "white");
  if (service?.notes == null) return <Text fontSize="sm" color="secondaryGray.500">—</Text>;
  return (
    <Flex align="center" gap="4px">
      <Icon as={MdStar} color="#FFD500" w="14px" h="14px" />
      <Text fontSize="sm" fontWeight="600" color={color}>{Number(service.notes).toFixed(1)}</Text>
    </Flex>
  );
}

/**
 * Renders the table header row.
 * @returns {React.ReactElement}
 */
function ListHead() {
  const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Thead>
      <Tr>
        {COLUMNS.map((label) => <Th key={label} {...HEAD_CELL} color={color}>{label}</Th>)}
      </Tr>
    </Thead>
  );
}

/**
 * Renders a single service row with inline visibility/test switches.
 * @param {Object} props - service, onToggleHidden, onToggleTest
 * @returns {React.ReactElement}
 */
function ServiceRow({ service, onToggleHidden, onToggleTest }) {
  const cellColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  return (
    <Tr _hover={{ bg: useColorModeValue("secondaryGray.100", "whiteAlpha.100") }}>
      <Td {...BODY_CELL}><ServiceLabelCell service={service} /></Td>
      <Td {...BODY_CELL}><OwnerCell service={service} /></Td>
      <Td {...BODY_CELL} color={cellColor}>{service?.country || "—"}</Td>
      <Td {...BODY_CELL} color={cellColor}>{formatServicePrice(service?.price)}</Td>
      <Td {...BODY_CELL}>
        <Switch isChecked={!service?.hidden} onChange={() => onToggleHidden?.(service)} colorScheme="brand" size="sm" />
      </Td>
      <Td {...BODY_CELL}>
        <Switch isChecked={Boolean(service?.test)} onChange={() => onToggleTest?.(service)} colorScheme="orange" size="sm" />
      </Td>
      <Td {...BODY_CELL}><NotesCell service={service} /></Td>
    </Tr>
  );
}

/**
 * Centered state message (loading spinner / error / empty).
 * @param {Object} props - loading (boolean), children (message node)
 * @returns {React.ReactElement}
 */
function StateBox({ loading, children }) {
  return (
    <Flex align="center" justify="center" py="40px">
      {loading ? <Spinner color="brand.500" /> : children}
    </Flex>
  );
}

/**
 * Services table — handles loading / error / empty internally.
 * @param {Object} props - items, loading, error, onToggleHidden, onToggleTest
 * @returns {React.ReactElement}
 */
function ServicesTable({ items, loading, error, onToggleHidden, onToggleTest }) {
  const empty = !items || items.length === 0;
  const errColor = useColorModeValue("red.500", "red.300");
  const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Card px="0" py="0" overflow="hidden">
      {loading && empty ? <StateBox loading><Box /></StateBox> : null}
      {!loading && error && empty ? (
        <StateBox><Text color={errColor} fontSize="sm">{typeof error === "string" ? error : "Erreur lors du chargement"}</Text></StateBox>
      ) : null}
      {!loading && !error && empty ? (
        <StateBox><Text color={emptyColor} fontSize="sm">Aucun service à afficher</Text></StateBox>
      ) : null}
      {!empty ? (
        <TableContainer>
          <Table variant="simple" size="sm">
            <ListHead />
            <Tbody>
              {items.map((s) => (
                <ServiceRow key={s.id} service={s} onToggleHidden={onToggleHidden} onToggleTest={onToggleTest} />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      ) : null}
    </Card>
  );
}

export default ServicesTable;
